<!-- src/components/Notifications/AlertsHubModal.vue -->
<template>
  <div v-if="isOpen" class="fixed inset-0 z-[9998] flex items-center justify-center bg-black/30">
    <div class="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-xl bg-white shadow-xl">
      <!-- Header -->
      <div class="flex items-center justify-between border-b px-4 py-3">
        <h2 class="text-sm font-semibold text-gray-800">Alerts Hub</h2>
        <button class="text-gray-400 hover:text-gray-700" @click="$emit('close')">
          <X class="h-5 w-5" />
        </button>
      </div>

      <!-- Tabs -->
      <div class="border-b px-4">
        <nav class="-mb-px flex space-x-6">
          <button
            @click="activeTab = 'active'"
            class="border-b-2 px-1 py-3 text-sm font-medium transition-colors"
            :class="
              activeTab === 'active'
                ? 'border-brand-blue text-brand-blue'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
            "
          >
            Active
            <span
              v-if="activeAlerts.length"
              class="ml-1.5 rounded-full bg-red-100 px-1.5 py-0.5 text-xs text-red-700"
            >
              {{ activeAlerts.length }}
            </span>
          </button>
          <button
            @click="activeTab = 'history'"
            class="border-b-2 px-1 py-3 text-sm font-medium transition-colors"
            :class="
              activeTab === 'history'
                ? 'border-brand-blue text-brand-blue'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
            "
          >
            History
            <span
              v-if="historyAlerts.length"
              class="ml-1.5 rounded-full bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600"
            >
              {{ historyAlerts.length }}
            </span>
          </button>
        </nav>
      </div>

      <!-- Active Tab Content -->
      <div v-if="activeTab === 'active'" class="flex-1 overflow-y-auto">
        <!-- Filters for active (optional - can add later) -->

        <div
          v-if="activeAlerts.length === 0"
          class="flex flex-col items-center justify-center py-12 text-gray-500"
        >
          <CheckCircle2 class="mb-3 h-12 w-12 text-green-400" />
          <p class="text-sm font-medium">All caught up!</p>
          <p class="text-xs text-gray-400">No active alerts</p>
        </div>

        <div v-else class="divide-y">
          <!-- Group by date -->
          <div v-for="group in activeGroupedByDate" :key="group.label" class="px-4">
            <div
              class="sticky top-0 bg-white py-2 text-xs font-semibold uppercase tracking-wide text-gray-500"
            >
              {{ group.label }}
            </div>
            <div class="space-y-2 pb-3">
              <AlertCard
                v-for="alert in group.alerts"
                :key="alert.id"
                :alert="alert"
                mode="active"
                @view="handleView"
                @notes="handleNotes"
                @dismiss="handleDismiss"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- History Tab Content -->
      <div v-if="activeTab === 'history'" class="flex-1 overflow-y-auto">
        <!-- Filters -->
        <div class="flex flex-wrap items-center gap-3 border-b bg-gray-50 px-4 py-3">
          <select v-model="historyFilter.kind" class="form-select w-36 py-1.5 text-xs">
            <option value="">All Types</option>
            <option v-for="kind in availableKinds" :key="kind.value" :value="kind.value">
              {{ kind.label }}
            </option>
          </select>
          <input
            v-model="historyFilter.search"
            type="text"
            placeholder="Search customer..."
            class="form-input w-40 py-1.5 text-xs"
          />
          <span class="ml-auto text-xs text-gray-400"> Last 10 days </span>
        </div>

        <div v-if="loadingHistory" class="flex items-center justify-center py-12">
          <Loader2 class="h-6 w-6 animate-spin text-brand-blue" />
        </div>

        <div
          v-else-if="filteredHistory.length === 0"
          class="flex flex-col items-center justify-center py-12 text-gray-500"
        >
          <Archive class="mb-3 h-12 w-12 text-gray-300" />
          <p class="text-sm font-medium">No history</p>
          <p class="text-xs text-gray-400">
            Dismissed alerts from the last 10 days will appear here
          </p>
        </div>

        <div v-else class="divide-y">
          <div v-for="group in historyGroupedByDate" :key="group.label" class="px-4">
            <div
              class="sticky top-0 bg-white py-2 text-xs font-semibold uppercase tracking-wide text-gray-500"
            >
              {{ group.label }}
            </div>
            <div class="space-y-2 pb-3">
              <AlertCard
                v-for="alert in group.alerts"
                :key="alert.id"
                :alert="alert"
                mode="history"
                @restore="handleRestore"
                @delete="handleDelete"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="flex items-center justify-between border-t bg-gray-50 px-4 py-3">
        <div class="flex items-center gap-3">
          <button
            v-if="activeTab === 'active' && activeAlerts.length > 0"
            class="text-xs text-gray-600 hover:text-gray-900 hover:underline"
            @click="handleClearAll"
          >
            Clear All Active
          </button>
          <button
            v-if="activeTab === 'history' && historyAlerts.length > 0"
            class="text-xs text-red-600 hover:text-red-800 hover:underline"
            @click="handleDeleteAllHistory"
          >
            Delete All History
          </button>
        </div>
        <button
          class="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
          @click="openSettings"
        >
          <Settings class="h-3 w-3" />
          Alert Settings
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  runTransaction,
  doc,
  updateDoc,
  deleteDoc,
  writeBatch,
  Timestamp,
} from 'firebase/firestore'
import { db } from '@/services/firebaseconfig'
import { useUserStore } from '@/stores/userStore'
import { useToast } from '@/composables/useToast'
import { X, CheckCircle2, Archive, Loader2, Settings } from 'lucide-vue-next'
import AlertCard from './AlertCard.vue'

const props = defineProps({
  isOpen: { type: Boolean, default: false },
  alerts: { type: Array, default: () => [] }, // Active alerts passed from parent
})

const emit = defineEmits(['close', 'clear-all', 'dismiss', 'view', 'notes', 'restore'])

const router = useRouter()
const userStore = useUserStore()
const toast = useToast()

const activeTab = ref('active')
const historyAlerts = ref([])
const loadingHistory = ref(false)

const historyFilter = ref({
  kind: '',
  search: '',
})

// Available kinds for filter dropdown
const availableKinds = [
  { value: 'customerCompleted', label: 'Customer Completed' },
  { value: 'customerCanceled', label: 'Customer Canceled' },
  { value: 'customerOnHold', label: 'Customer On Hold' },
  { value: 'customerReleasedFromHold', label: 'Released from Hold' },
  { value: 'customerReactivated', label: 'Customer Reactivated' },
  { value: 'taskRejected', label: 'Task Rejected' },
  { value: 'noteTagged', label: 'Tagged in Note' },
  { value: 'leadPool', label: 'Lead Pool' },
  { value: 'voicemail', label: 'Voicemail' },
  { value: 'missedCallBrowser', label: 'Missed Call' },
  { value: 'calendarEventAccepted', label: 'Calendar Event Accepted' },
  { value: 'calendarEventDeclined', label: 'Calendar Event Declined' },
  { value: 'calendarEventTentative', label: 'Calendar Event Tentative' },
  { value: 'unreadChat', label: 'Chat Message' },
  { value: 'communicationResolutionNeeded', label: 'Response Needed' },
  { value: 'inventoryLowStock', label: 'Low Stock Warning' },
  { value: 'inventoryCritical', label: 'Critical Stock Alert' },
  { value: 'kittingProblem', label: 'Kitting Problem' },
]

// Active alerts from props
const activeAlerts = computed(() => props.alerts || [])

// Filter history
const filteredHistory = computed(() => {
  let list = historyAlerts.value

  if (historyFilter.value.kind) {
    list = list.filter(a => a.kind === historyFilter.value.kind)
  }

  if (historyFilter.value.search) {
    const q = historyFilter.value.search.toLowerCase()
    list = list.filter(a => (a.customerName || '').toLowerCase().includes(q))
  }

  return list
})

// Group alerts by date, returning sorted array of { label, date, alerts }
const groupByDate = alerts => {
  const groupsMap = new Map() // Use Map to preserve insertion order
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  for (const alert of alerts) {
    const alertDate = alert.at || alert.dismissedAt
    if (!alertDate) continue

    const d = new Date(alertDate)
    d.setHours(0, 0, 0, 0)
    const dateKey = d.getTime() // Use timestamp as key for proper sorting

    let label
    if (d.getTime() === today.getTime()) {
      label = 'Today'
    } else if (d.getTime() === yesterday.getTime()) {
      label = 'Yesterday'
    } else {
      label = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    }

    if (!groupsMap.has(dateKey)) {
      groupsMap.set(dateKey, { label, date: d, alerts: [] })
    }
    groupsMap.get(dateKey).alerts.push(alert)
  }

  // Sort by date descending (most recent first) and return as array
  return Array.from(groupsMap.values()).sort((a, b) => b.date.getTime() - a.date.getTime())
}

const activeGroupedByDate = computed(() => groupByDate(activeAlerts.value))
const historyGroupedByDate = computed(() => groupByDate(filteredHistory.value))

// Load history when tab changes or modal opens
const loadHistory = async () => {
  const uid = userStore.user?.uid
  if (!uid) return

  loadingHistory.value = true
  try {
    // Get dismissed alerts from last 10 days
    const tenDaysAgo = new Date()
    tenDaysAgo.setDate(tenDaysAgo.getDate() - 10)

    const historyRef = query(
      collection(db, `users/${uid}/ephemeralAlerts`),
      where('dismissed', '==', true),
      where('dismissedAt', '>=', Timestamp.fromDate(tenDaysAgo)),
      orderBy('dismissedAt', 'desc')
    )

    const snap = await getDocs(historyRef)
    const rows = []
    snap.forEach(d => {
      const x = d.data() || {}
      rows.push({
        id: d.id,
        kind: x.kind || 'alert',
        customerId: x.customerId || null,
        customerName: x.customerName || null,
        // ✅ Map new fields for proper display in History
        title: x.title || null,
        subtitle: x.subtitle || null,
        extra: x.extra || {},
        monitorJobId: x.monitorJobId || null,
        monitorIncidentId: x.monitorIncidentId || null,
        resolved: x.resolved === true,
        // Voice/call fields (spread at root by backend)
        fromNumber: x.fromNumber || null,
        callerIdName: x.callerIdName || null,

        holdIsInternal: !!x.holdIsInternal,
        actorUid: x.actorUid || null,
        actorEmail: x.actorEmail || null,
        taskId: x.taskId || null,
        taskDisplayName: x.taskDisplayName || null,
        at: x.at?.toDate?.() || null,
        dismissedAt: x.dismissedAt?.toDate?.() || null,
        // Calendar event fields
        attendeeName: x.attendeeName || null,
        eventSummary: x.eventSummary || null,
      })
    })

    historyAlerts.value = rows
  } catch (e) {
    console.error('[AlertsHubModal] Failed to load history:', e)
    toast.error('Failed to load alert history')
  } finally {
    loadingHistory.value = false
  }
}

// Watch for tab changes
watch(activeTab, newTab => {
  if (newTab === 'history' && historyAlerts.value.length === 0) {
    loadHistory()
  }
})

// Watch for modal open
watch(
  () => props.isOpen,
  isOpen => {
    if (isOpen) {
      activeTab.value = 'active'
      if (historyAlerts.value.length === 0) {
        // Pre-load history in background
        loadHistory()
      }
    }
  }
)

// Handlers
const handleView = alert => {
  emit('view', alert)
}

const handleNotes = alert => {
  emit('notes', alert)
}

const handleDismiss = async alert => {
  const uid = userStore.user?.uid
  if (!uid || !alert?.id) return

  try {
    const ref = doc(db, `users/${uid}/ephemeralAlerts/${alert.id}`)
    await updateDoc(ref, {
      dismissed: true,
      dismissedAt: Timestamp.now(),
    })

    // Add to local history
    historyAlerts.value.unshift({
      ...alert,
      dismissedAt: new Date(),
    })
  } catch (e) {
    console.error('[AlertsHubModal] Failed to dismiss:', e)
    toast.error('Failed to dismiss alert')
  }
}

const handleClearAll = async () => {
  const uid = userStore.user?.uid
  if (!uid) return

  // IMPORTANT: Capture alerts BEFORE the batch commit
  // Once committed, the real-time listener will filter them out of activeAlerts
  const alertsToMove = [...activeAlerts.value]
  if (alertsToMove.length === 0) return

  try {
    const batch = writeBatch(db)
    const now = Timestamp.now()

    for (const alert of alertsToMove) {
      if (!alert.id) continue
      const ref = doc(db, `users/${uid}/ephemeralAlerts/${alert.id}`)
      batch.update(ref, { dismissed: true, dismissedAt: now })
    }

    await batch.commit()

    // Add all to local history using the captured list
    const nowDate = new Date()
    historyAlerts.value = [
      ...alertsToMove.map(a => ({ ...a, dismissedAt: nowDate })),
      ...historyAlerts.value,
    ]

    toast.success('All alerts cleared')
  } catch (e) {
    console.error('[AlertsHubModal] Failed to clear all:', e)
    toast.error('Failed to clear alerts')
  }
}

const handleRestore = async alert => {
  if (alert?.kind === 'systemMonitoring' && alert.resolved) return
  const uid = userStore.user?.uid
  if (!uid || !alert?.id) return

  try {
    const ref = doc(db, `users/${uid}/ephemeralAlerts/${alert.id}`)
    if (alert.kind === 'systemMonitoring') {
      const restored = await runTransaction(db, async transaction => {
        const current = await transaction.get(ref)
        if (!current.exists() || current.data().resolved) return false
        transaction.update(ref, { dismissed: false, dismissedAt: null })
        return true
      })
      if (!restored) {
        await loadHistory()
        toast.success('This incident has already been resolved')
        return
      }
    } else {
      await updateDoc(ref, { dismissed: false, dismissedAt: null })
    }

    // Remove from local history
    historyAlerts.value = historyAlerts.value.filter(a => a.id !== alert.id)

    toast.success('Alert restored')
  } catch (e) {
    console.error('[AlertsHubModal] Failed to restore:', e)
    toast.error('Failed to restore alert')
  }
}

const handleDelete = async alert => {
  const uid = userStore.user?.uid
  if (!uid || !alert?.id) return

  try {
    const ref = doc(db, `users/${uid}/ephemeralAlerts/${alert.id}`)
    await deleteDoc(ref)

    // Remove from local history
    historyAlerts.value = historyAlerts.value.filter(a => a.id !== alert.id)

    toast.success('Alert deleted')
  } catch (e) {
    console.error('[AlertsHubModal] Failed to delete:', e)
    toast.error('Failed to delete alert')
  }
}

const handleDeleteAllHistory = async () => {
  if (!confirm('Delete all alert history? This cannot be undone.')) return

  const uid = userStore.user?.uid
  if (!uid) return

  try {
    const batch = writeBatch(db)

    for (const alert of historyAlerts.value) {
      if (!alert.id) continue
      const ref = doc(db, `users/${uid}/ephemeralAlerts/${alert.id}`)
      batch.delete(ref)
    }

    await batch.commit()
    historyAlerts.value = []

    toast.success('History cleared')
  } catch (e) {
    console.error('[AlertsHubModal] Failed to delete history:', e)
    toast.error('Failed to delete history')
  }
}

const openSettings = () => {
  emit('close')
  router.push({ name: 'Settings' })
}
</script>
